import { useCallback, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { EditTicketType, TypeGroup } from '@type/EditionTypes';
import type { Date } from '@type/EventTypes';

import { getQuantity, formatCurrency, formatDate, getDiscountPrice, getTypeMode } from '@lib/helpers';

import { useAppSelector } from '@redux/hooks';

import { EditButton } from '@components/EditButton';
import { Icon } from '@components/Icon';
import { EditTypeModal } from '@components/modals/EditTypeModal';
import { EmptyTickets } from '@components/EmptyStates';
import { RiArrowDownSLine, RiNotification2Fill } from '@remixicon/react';
import { SubscribeModal } from '@components/modals/SubscribeModal';

type TicketsModuleProps = {
    types: EditTicketType[];
    title?: string | null;
    showId?: string | undefined;
    showDate?: Date | undefined;
    showName?: string | undefined;
    hideTitle?: boolean;
};

type GroupProps = {
    group: boolean;
    open: boolean;
};
type TypeItemProps = {
    brandingColor: string;
};
type GroupHeaderProps = {
    open: boolean;
};
type GroupContentProps = {
    group: boolean;
};

export const Tickets: React.FC<TicketsModuleProps> = (props) => {
    const { types, showId, showDate, showName } = props;
    const { cart, edition, event } = useAppSelector((state) => {
        return { cart: state.store.cart, edition: state.store.edition, event: state.store.event };
    });
    const [activeType, setActiveType] = useState<EditTicketType | undefined>(undefined);
    const [activeGroups, setActiveGroups] = useState<TypeGroup[] | undefined>(undefined);

    const [subscribeModalOpen, setSubscribeModalOpen] = useState<boolean>(false);
    const [subscribeModalType, setSubscribeModalType] = useState<EditTicketType | undefined>(undefined);

    const { lang } = useParams();
    const { t } = useTranslation();

    const typeMode = useMemo(() => getTypeMode(edition), [edition]);
    const allowSubscriptions = useMemo(() => edition?.allow_subscriptions, [edition]);

    const handleEditType = (type: EditTicketType) => {
        if (type.status === 'soldout' && allowSubscriptions) {
            setSubscribeModalOpen(true);
            setSubscribeModalType(type);
        } else {
            setActiveType(type);
        }
    };

    const closeModal = () => {
        setActiveType(undefined);
    };

    // Filter types with group
    const typesWithGroup = useMemo(() => {
        return types.filter((type) => !!type.type_group);
    }, [types]);

    const toggleActiveGroup = useCallback(
        (group: TypeGroup) => {
            if (activeGroups && activeGroups.find((g) => g.id === group.id)) {
                setActiveGroups(activeGroups.filter((g) => g.id !== group.id));
            } else {
                setActiveGroups((prev) => (prev ? [...prev, group] : [group]));
            }
        },
        [activeGroups],
    );

    // Get all type groups
    const typeGroups = useMemo(() => {
        let groups: TypeGroup[] = typesWithGroup.map((type) => type.type_group as TypeGroup);
        // Filter out duplicates (by name, by id in the future)
        groups = groups.filter((group, index, self) => {
            return (
                index ===
                self.findIndex((g) => {
                    return g.id === group.id;
                })
            );
        });
        // Sort by key 'position'
        return groups.sort((a, b) => {
            return a.position - b.position;
        });
    }, [typesWithGroup]);

    const getTypeMessage = useCallback(
        (type: EditTicketType) => {
            if (!type.sales.status) {
                // This is when a type is not available for sale

                if (!type.sales.start && !type.sales.end) {
                    return t('tickets.message.not_available');
                }

                const now = +new Date();
                if (type.sales.start && +new Date(type.sales.start) > now) {
                    return t('tickets.message.available_from', { date: formatDate(type.sales.start, false, lang) });
                }

                if (type.sales.end && +new Date(type.sales.end) < now) {
                    return t('tickets.message.no_more_available');
                }

                return null;
            }

            if (type.status === 'low') {
                return type.stock ? t('tickets.message.stock', { stock: type.stock }) : t('tickets.message.status_low');
            }

            if (type.stock) {
                return t('tickets.message.stock', { stock: type.stock });
            }

            return null;
        },
        [lang, t],
    );

    const handleCloseSubscribeModal = useCallback(() => {
        setSubscribeModalOpen(false);
        setSubscribeModalType(undefined);
    }, []);

    const renderTicketList = useCallback(
        (group: null | TypeGroup) => {
            if (!event) {
                return null;
            }
            const filteredTypes = types.filter((type) => {
                if (!group) {
                    return !type.type_group;
                }
                return type.type_group && type.type_group.id === group.id;
            });

            // Hide empty groups
            if (!filteredTypes.length) {
                return null;
            }

            const isGroup = !!group;
            const isOpen = isGroup && !!activeGroups && !!activeGroups.find((g) => g.id === group.id);

            //const groupTitle = group ? group.name : t('tickets.title');

            return (
                <StyledGroup group={isGroup} open={isOpen}>
                    {isGroup && (
                        <>
                            <StyledGroupHeader onClick={() => toggleActiveGroup(group)} open={isOpen}>
                                <StyledGroupTitle>{group.name}</StyledGroupTitle>
                                <RiArrowDownSLine />
                            </StyledGroupHeader>
                        </>
                    )}

                    {(!isGroup || isOpen) && (
                        <StyledGroupContent group={isGroup}>
                            {group?.subtitle && <StyledGroupSubtitle>{group.subtitle}</StyledGroupSubtitle>}

                            <StyledTicketList>
                                {filteredTypes.map((type) => {
                                    // type.status === 'available' | 'soldout' | 'low' | 'not_available'
                                    const typeQuantity = getQuantity(type, cart, type.showId);
                                    const priceWithDiscount = getDiscountPrice(type, cart);
                                    const message = getTypeMessage(type);
                                    return (
                                        <StyledTicketItem key={type.id + '1'} brandingColor={type.branding.color}>
                                            <StyledTicketBox>
                                                <div onClick={() => handleEditType(type)}>
                                                    <StyledTicketName> {type.name} </StyledTicketName>
                                                    <StyledTicketPrice>
                                                        {priceWithDiscount && priceWithDiscount > 0
                                                            ? formatCurrency(priceWithDiscount, lang, event.currency)
                                                            : t('tickets.free')}
                                                        {!!type.price && priceWithDiscount < type.price && (
                                                            <span className="original-price">
                                                                {formatCurrency(type.price, lang, event.currency)}
                                                            </span>
                                                        )}
                                                        {!!type.fee && !!priceWithDiscount && (
                                                            <span className="fee">
                                                                {t('tickets.fee', {
                                                                    fee: formatCurrency(type.fee, lang, event.currency),
                                                                })}
                                                            </span>
                                                        )}
                                                        {!!type.price && edition?.channel.hide_vat && (
                                                            <span className="vat_info">{t('tickets.VAT_excl')}</span>
                                                        )}
                                                    </StyledTicketPrice>
                                                </div>
                                                {(type.status === 'available' || type.status === 'low') && (
                                                    <EditButton
                                                        buttonType={typeQuantity ? 'edit' : 'ghost'}
                                                        onClick={() => handleEditType(type)}
                                                    >
                                                        {typeQuantity ? typeQuantity : <Icon name="add-fill" />}
                                                    </EditButton>
                                                )}
                                                {type.status === 'soldout' && (
                                                    <EditButton buttonType="text" onClick={() => handleEditType(type)}>
                                                        {allowSubscriptions ? (
                                                            <>
                                                                <RiNotification2Fill size=".75rem" />
                                                                {t('tickets.waiting_list')}
                                                            </>
                                                        ) : (
                                                            t('tickets.soldout')
                                                        )}
                                                    </EditButton>
                                                )}
                                                {type.status === 'not_available' && (
                                                    <EditButton buttonType="text" onClick={() => handleEditType(type)}>
                                                        {t('tickets.info')}
                                                    </EditButton>
                                                )}
                                            </StyledTicketBox>
                                            {message && <StyledTicketInfo>{message}</StyledTicketInfo>}
                                        </StyledTicketItem>
                                    );
                                })}
                            </StyledTicketList>
                        </StyledGroupContent>
                    )}
                </StyledGroup>
            );
        },
        [types, t, cart, getTypeMessage, lang, event, edition, activeGroups, toggleActiveGroup],
    );

    // When event is not loaded yet
    if (!event) {
        return null;
    }

    // When show has no types
    if (types.length === 0 && typeMode !== 'split') {
        return <EmptyTickets showId={showId} />;
    }
    return (
        <>
            {/* Tickets without group */}
            {renderTicketList(null)}

            {/* Render groups */}
            {typeGroups.map((group) => {
                return <div key={group.id}>{renderTicketList(group)}</div>;
            })}
            <EditTypeModal
                type={activeType}
                closeModal={closeModal}
                cart={cart}
                showDate={showDate}
                showName={showName}
                typeMode={getTypeMode(edition)}
                currency={event.currency}
                edition={edition}
            />
            <SubscribeModal
                open={subscribeModalOpen}
                type={subscribeModalType}
                closeModal={handleCloseSubscribeModal}
            />
        </>
    );
};

const StyledGroup = styled.div<GroupProps>`
    margin-bottom: 1rem;
    ${(props) =>
        props.group &&
        css`
            background-color: ${(props) => props.theme.helpers.transparancy(props.theme.colors.primary, 0.95)};
            border: 1px solid transparent;
            border-radius: 0.5rem;
            overflow: hidden;
            margin-bottom: 0.5rem;
        `}
    // No background when props.open is true
    ${(props) =>
        props.open &&
        css`
            background-color: transparent;
            border: 1px solid ${(props) => props.theme.helpers.transparancy(props.theme.colors.primary, 0.8)};
        `}
`;

const StyledGroupHeader = styled.button<GroupHeaderProps>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    border: none;
    background: none;
    text-align: left;
    font-family: var(--theme-font-family);
    padding: 0.75rem 1rem;
    padding-right: 1.25rem;
    gap: 1rem;
    cursor: pointer;
    color: var(--theme-color-primary);
    transition: all 200ms ease;

    svg {
        width: 1.5rem;
        transition: all 200ms ease;
        height: 1.5rem;
    }

    &:hover {
        background-color: ${(props) => props.theme.helpers.transparancy(props.theme.colors.primary, 0.9)};
        color: ${(props) => props.theme.helpers.shades(props.theme.colors.primary, 2)};
    }

    ${(props) =>
        props.open &&
        css`
            border-top: 0;
            background-color: ${(props) => props.theme.helpers.transparancy(props.theme.colors.primary, 0.95)};

            svg {
                transform: rotate(180deg);
            }
        `}
`;

const StyledGroupTitle = styled.h3`
    font-weight: 700;
    color: inherit;
    font-size: var(--theme-font-size-2);
    margin: 0;
    flex: 1;
    line-height: 1.2;
`;

const StyledGroupSubtitle = styled.p`
    font-size: var(--theme-font-size-min-1);
    margin-top: 1rem;
    line-height: 1.2;
    color: var(--theme-font-color-subtle);
    margin-bottom: 1rem;
`;

const StyledGroupContent = styled.div<GroupContentProps>`
    ${(props) =>
        props.group &&
        css`
            padding: 0 1rem;
        `}
`;

const StyledTicketList = styled.ul``;

const StyledTicketItem = styled.li<TypeItemProps>`
    padding: 1rem 0 1rem 1rem;
    border-bottom: 1px solid var(--theme-lines);
    position: relative;

    &:last-of-type {
        border-bottom: none;
    }

    ${(props) =>
        props.brandingColor &&
        css`
            &:before {
                content: '';
                height: calc(100% - 2rem);
                width: 0.25rem;
                border-radius: 0.125rem;
                position: absolute;
                top: 1rem;
                left: 0;
                background-color: ${props.brandingColor};
            }
        `}
`;

const StyledTicketBox = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
`;

const StyledTicketName = styled.h5`
    font-size: 1.125rem;
    font-weight: 700;
    margin-bottom: 0.25rem;
    color: var(--theme-font-color-heading);
    cursor: pointer;

    &:hover {
        color: ${(props) => props.theme.helpers.shades(props.theme.colors.primary, 2)};
    }
`;
const StyledTicketPrice = styled.h6`
    font-size: 1.125rem;
    font-weight: 400;
    color: var(--theme-font-color);

    span.original-price {
        display: inline-block;
        margin-left: 0.375rem;
        color: var(--theme-font-color);
        font-size: 1rem;
        text-decoration: line-through;
    }

    span.fee,
    span.vat_info {
        display: inline-block;
        margin-left: 0.25rem;
        color: var(--theme-font-color-subtle);
        font-size: 0.75rem;
    }
`;
const StyledTicketInfo = styled.p`
    text-transform: uppercase;
    color: var(--theme-font-color-subtle);
    font-weight: 700;
    font-size: 0.75rem;
    margin-top: 0.5rem;
`;
